import styled from '@/styles';

import HelpTourButton from './HelpTourButton';

const HeaderShell = styled('div', {
  position: 'sticky',
  top: 0,
  background: '#fff',
  borderBottom: '1px solid #EAEAEA',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
  height: 56,
  zIndex: 10,
  boxSizing: 'border-box',
  flexShrink: 0,
});

const HeaderTitle = styled('p', {
  fontSize: 18,
  fontWeight: 700,
  fontFamily: "'Manrope', sans-serif",
  color: '#1C1C1C',
  margin: 0,
  whiteSpace: 'nowrap',
});

const StepperRow = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 24,
});

const Connector = styled('div', {
  position: 'absolute',
  top: 12,
  left: 40,
  right: 40,
  height: 1,
  background: '#DADADA',
});

const StepCell = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  width: 56,
  position: 'relative',
  zIndex: 1,
});

const StepLabel = styled('span', {
  fontSize: 12,
  fontFamily: "'Manrope', sans-serif",
  whiteSpace: 'nowrap',
  variants: {
    state: {
      done: { fontWeight: 400, color: '#979797' },
      active: { fontWeight: 600, color: '#1C1C1C' },
      upcoming: { fontWeight: 400, color: '#979797' },
    },
  },
});

const StepDoneCircle = styled('div', {
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#44A735',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const StepActiveCircle = styled('div', {
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#44A735',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 0 0 5px rgba(68,167,53,0.15)',
});

const StepUpcomingCircle = styled('div', {
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#fff',
  border: '1px solid #DADADA',
});

const StepActiveNumber = styled('span', {
  fontSize: 14,
  fontWeight: 700,
  color: '#fff',
  fontFamily: "'Manrope', sans-serif",
  lineHeight: 1,
});

// Legacy v1 `Progress` keeps the step number visible after a step is
// completed (see CreateGigOrder/JobEditor/TopBar/Progress) — it never
// swaps the digit for a check. Mirror that here so traversed steps stay
// numbered.
const StepDoneNumber = styled('span', {
  fontSize: 14,
  fontWeight: 700,
  color: '#fff',
  fontFamily: "'Manrope', sans-serif",
  lineHeight: 1,
});

const HeaderActions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
});

const CloseButton = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1C1C1C"
    strokeWidth="2"
    style={{ width: 24, height: 24 }}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

type WizardHeaderProps = {
  title: string;
  steps: string[];
  currentStep: number;
  onClose: () => void;
  onHelp: () => void;
};

const WizardHeader = ({
  title,
  steps,
  currentStep,
  onClose,
  onHelp,
}: WizardHeaderProps) => (
  <HeaderShell>
    <HeaderTitle>{title}</HeaderTitle>
    <StepperRow data-tour="orderflow-stepper">
      <Connector />
      {steps.map((label, index) => {
        const state =
          index < currentStep
            ? 'done'
            : index === currentStep
              ? 'active'
              : 'upcoming';
        return (
          <StepCell key={label}>
            {state === 'done' ? (
              <StepDoneCircle>
                <StepDoneNumber>{index + 1}</StepDoneNumber>
              </StepDoneCircle>
            ) : state === 'active' ? (
              <StepActiveCircle>
                <StepActiveNumber>{index + 1}</StepActiveNumber>
              </StepActiveCircle>
            ) : (
              <StepUpcomingCircle />
            )}
            <StepLabel state={state}>{label}</StepLabel>
          </StepCell>
        );
      })}
    </StepperRow>
    <HeaderActions>
      <HelpTourButton onClick={onHelp} />
      <CloseButton type="button" onClick={onClose} aria-label="Close">
        <XIcon />
      </CloseButton>
    </HeaderActions>
  </HeaderShell>
);

export default WizardHeader;
